import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Twitter, Users, Eye } from "lucide-react";
import janetLogo from "@/assets/janet-marketing-logo.png";
import { useVisitorCounter } from "@/hooks/Usevisitorcounter";

export function Footer() {
  const visitorStats = useVisitorCounter();

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-turquoise-very-dark via-[hsl(186,80%,10%)] to-turquoise-very-dark">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-turquoise/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-maroon/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Main Footer Content */}
      <div className="container py-12 md:py-16 relative z-10 px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={janetLogo} 
                alt="Janet Marketing Academy Logo" 
                className="h-10 w-auto"
              />
              <span className="text-white font-heading text-sm font-semibold tracking-wider">ACADEMY</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Training the next generation of digital marketing professionals with practical, hands-on skills.
            </p>
            
            <div className="flex items-center gap-3">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 hover:border-turquoise/50 transition-all duration-300 flex items-center justify-center group"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4 text-gray-300 group-hover:text-turquoise transition-colors" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 hover:border-turquoise/50 transition-all duration-300 flex items-center justify-center group"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-4 h-4 text-gray-300 group-hover:text-turquoise transition-colors" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 hover:border-turquoise/50 transition-all duration-300 flex items-center justify-center group"
                aria-label="Connect on LinkedIn"
              >
                <Linkedin className="w-4 h-4 text-gray-300 group-hover:text-turquoise transition-colors" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 hover:border-turquoise/50 transition-all duration-300 flex items-center justify-center group"
                aria-label="Follow us on Twitter"
              >
                <Twitter className="w-4 h-4 text-gray-300 group-hover:text-turquoise transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#about" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  About Us
                </a>
              </li>
              <li>
                <a href="#courses" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  Our Courses
                </a>
              </li>
              <li>
                <a href="#testimonials" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  Success Stories
                </a>
              </li>
              <li>
                <a href="#faq" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  FAQs
                </a>
              </li>
            </ul>
          </div>

          {/* Training Programs */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">
              Training Programs
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  Content & Growth
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  Meta Ads Mastery
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  TikTok Marketing
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  WhatsApp Sales
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-turquoise" />
                </div>
                <span className="text-gray-300 text-sm">
                  Nairobi, Kenya
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone className="w-4 h-4 text-turquoise" />
                </div>
                <a href="tel:+254704544323" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  +254 704544323
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-white/5 to-white/10 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-turquoise" />
                </div>
                <a href="mailto:info@janetmarketing.com" className="text-gray-300 hover:text-turquoise transition-colors text-sm">
                  info@janetmarketing.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Visitor Counter Section */}
      <div className="border-t border-white/10 relative z-10">
        <div className="container py-6 px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-3 bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 rounded-lg px-4 py-2">
              <Eye className="w-4 h-4 text-turquoise" />
              <div className="text-center">
                <p className="text-xs text-gray-400 font-medium">Total Visits</p>
                <p className="text-lg font-bold text-white">{visitorStats.totalVisits.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 rounded-lg px-4 py-2">
              <Users className="w-4 h-4 text-turquoise" />
              <div className="text-center">
                <p className="text-xs text-gray-400 font-medium">Unique Visitors</p>
                <p className="text-lg font-bold text-white">{visitorStats.uniqueVisitors.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 relative z-10">
        <div className="container py-6 px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col items-center md:items-start gap-2">
              <p className="text-sm text-gray-400 text-center md:text-left">
                © {new Date().getFullYear()} Janet Marketing Academy. All rights reserved.
              </p>
              
              {/* Subtle developer credit with WhatsApp contact */}
              <a 
                href="https://wa.me/254796814154" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 opacity-25 hover:opacity-100 transition-all duration-500"
                title="Contact Developer"
              >
                <span className="text-[10px] text-gray-500 group-hover:text-turquoise transition-colors">
                  crafted by
                </span>
                <span className="text-[10px] font-mono text-gray-400 group-hover:text-turquoise transition-colors tracking-wider">
                  mdaucodes
                </span>
                <svg 
                  className="w-2.5 h-2.5 text-gray-500 group-hover:text-turquoise transition-colors" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-gray-400 hover:text-turquoise transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-gray-400 hover:text-turquoise transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-sm text-gray-400 hover:text-turquoise transition-colors">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </footer>
  );
}























// import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Twitter } from "lucide-react";
// import janetLogo from "@/assets/janet-marketing-logo.png";

// export function Footer() {
//   return (
//     <footer className="bg-secondary text-white">
//       {/* Main Footer Content */}
//       <div className="container py-12 md:py-16">
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
//           {/* Brand Column */}
//           <div className="lg:col-span-1">
//             <div className="flex items-center gap-3 mb-4">
//               <img 
//                 src={janetLogo} 
//                 alt="Janet Marketing Academy Logo" 
//                 className="h-10 w-auto"
//               />
//               <span className="text-white font-heading text-sm font-semibold tracking-wider">ACADEMY</span>
//             </div>
//             <p className="text-white/70 text-sm leading-relaxed mb-6">
//               Training the next generation of digital marketing professionals with practical, hands-on skills.
//             </p>
//             <div className="flex items-center gap-3">
//               <a 
//                 href="https://instagram.com" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="w-10 h-10 rounded-full bg-white/10 hover:bg-accent transition-colors flex items-center justify-center group"
//                 aria-label="Follow us on Instagram"
//               >
//                 <Instagram className="w-4 h-4 text-white/80 group-hover:text-secondary" />
//               </a>
//               <a 
//                 href="https://facebook.com" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="w-10 h-10 rounded-full bg-white/10 hover:bg-accent transition-colors flex items-center justify-center group"
//                 aria-label="Follow us on Facebook"
//               >
//                 <Facebook className="w-4 h-4 text-white/80 group-hover:text-secondary" />
//               </a>
//               <a 
//                 href="https://linkedin.com" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="w-10 h-10 rounded-full bg-white/10 hover:bg-accent transition-colors flex items-center justify-center group"
//                 aria-label="Connect on LinkedIn"
//               >
//                 <Linkedin className="w-4 h-4 text-white/80 group-hover:text-secondary" />
//               </a>
//               <a 
//                 href="https://twitter.com" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="w-10 h-10 rounded-full bg-white/10 hover:bg-accent transition-colors flex items-center justify-center group"
//                 aria-label="Follow us on Twitter"
//               >
//                 <Twitter className="w-4 h-4 text-white/80 group-hover:text-secondary" />
//               </a>
//             </div>
//           </div>

//           {/* Quick Links */}
//           <div>
//             <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
//             <ul className="space-y-3">
//               <li>
//                 <a href="#about" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   About Us
//                 </a>
//               </li>
//               <li>
//                 <a href="#courses" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   Our Courses
//                 </a>
//               </li>
//               <li>
//                 <a href="#testimonials" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   Success Stories
//                 </a>
//               </li>
//               <li>
//                 <a href="#faq" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   FAQs
//                 </a>
//               </li>
//             </ul>
//           </div>

//           {/* Training Programs */}
//           <div>
//             <h4 className="font-heading font-semibold text-white mb-4">Training Programs</h4>
//             <ul className="space-y-3">
//               <li>
//                 <a href="#" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   Social Media Marketing
//                 </a>
//               </li>
//               <li>
//                 <a href="#" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   Meta Ads Mastery
//                 </a>
//               </li>
//               <li>
//                 <a href="#" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   TikTok Marketing
//                 </a>
//               </li>
//               <li>
//                 <a href="#" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   Content Creation
//                 </a>
//               </li>
//             </ul>
//           </div>

//           {/* Contact Info */}
//           <div>
//             <h4 className="font-heading font-semibold text-white mb-4">Contact Us</h4>
//             <ul className="space-y-4">
//               <li className="flex items-start gap-3">
//                 <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
//                   <MapPin className="w-4 h-4 text-accent" />
//                 </div>
//                 <span className="text-white/70 text-sm">
//                   Nairobi, Kenya
//                 </span>
//               </li>
//               <li className="flex items-start gap-3">
//                 <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
//                   <Phone className="w-4 h-4 text-accent" />
//                 </div>
//                 <a href="tel:+254704544323" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   +254 704544323
//                 </a>
//               </li>
//               <li className="flex items-start gap-3">
//                 <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
//                   <Mail className="w-4 h-4 text-accent" />
//                 </div>
//                 <a href="mailto:info@janetmarketing.com" className="text-white/70 hover:text-accent transition-colors text-sm">
//                   info@janetmarketing.com
//                 </a>
//               </li>
//             </ul>
//           </div>
//         </div>
//       </div>

//       {/* Bottom Bar */}
//       <div className="border-t border-white/10">
//         <div className="container py-6">
//           <div className="flex flex-col md:flex-row items-center justify-between gap-4">
//             <div className="flex flex-col items-center md:items-start gap-1">
//               <p className="text-sm text-white/60 text-center md:text-left">
//                 © {new Date().getFullYear()} Janet Marketing Academy. All rights reserved.
//               </p>
//               {/* Subtle developer credit with WhatsApp contact */}
//               <a 
//                 href="https://wa.me/254796814154" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="group flex items-center gap-1.5 opacity-25 hover:opacity-100 transition-all duration-500"
//                 title="Contact Developer"
//               >
//                 <span className="text-[10px] text-white/40 group-hover:text-accent transition-colors">
//                   crafted by
//                 </span>
//                 <span className="text-[10px] font-mono text-white/50 group-hover:text-accent transition-colors tracking-wider">
//                   mdaucodes
//                 </span>
//                 <svg 
//                   className="w-2.5 h-2.5 text-white/40 group-hover:text-accent transition-colors" 
//                   viewBox="0 0 24 24" 
//                   fill="currentColor"
//                 >
//                   <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
//                 </svg>
//               </a>
//             </div>
//             <div className="flex items-center gap-6">
//               <a href="#" className="text-sm text-white/60 hover:text-accent transition-colors">
//                 Privacy Policy
//               </a>
//               <a href="#" className="text-sm text-white/60 hover:text-accent transition-colors">
//                 Terms of Service
//               </a>
//               <a href="#" className="text-sm text-white/60 hover:text-accent transition-colors">
//                 Cookie Policy
//               </a>
//             </div>
//           </div>
//         </div>
//       </div>
//     </footer>
//   );
// }